import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import {
    Outlet,
    useLocation,
    useNavigate,
    useSearchParams,
} from "@remix-run/react";

import { useLinkPractitioner } from "~/hooks/mutations/use-link-practitioner";
import { useRegisterPractitioner } from "~/hooks/mutations/use-register-practitioner";
import { useIsPendingPractitioner } from "~/hooks/use-pending-practitioner";
import { logger } from "~/lib/logger";
import { useUserProfile } from "~/lib/user";

import Layout from "./layout";

// Context for onboarding flow
type OnboardingContextType = {
    currentStep: string;
    onStepComplete: (step: string) => void;
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(
    undefined,
);

// Hook to use the onboarding context
export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        return {
            onStepComplete: () => {
                //noop
            },
        };
    }
    return context;
};

const OnboardingWrapper = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPendingPractitioner, setPendingPractitioner] =
        useIsPendingPractitioner();
    const { data: profile, isLoading } = useUserProfile();
    const {
        mutateAsync: registerPractitioner,
        isPending: isRegisteringPractitioner,
    } = useRegisterPractitioner();
    const [currentStep, setCurrentStep] = useState<string>("");
    const [searchParams] = useSearchParams();
    const clientInviteToken = searchParams.get("clientInviteToken");
    const practitionerInviteToken = searchParams.get("practitionerInviteToken");
    const { mutateAsync: linkPractitioner, isPending: isLinkingPractitioner } =
        useLinkPractitioner();

    useEffect(() => {
        if (clientInviteToken) {
            linkPractitioner({ token: clientInviteToken });
        }
    }, [clientInviteToken, linkPractitioner]);

    const navigateToNextOnboardingStep = useCallback(() => {
        if (!profile) {
            return;
        }

        const onboardingCompleted = profile.onboarding_status === "completed";
        const isOnboardingRoute = location.pathname.startsWith("/onboarding");
        const isAuthRoute =
            location.pathname.startsWith("/landing") ||
            location.pathname === "/login" ||
            location.pathname === "/signup";

        if (!onboardingCompleted && !isOnboardingRoute && !isAuthRoute) {
            logger.info("Redirecting to terms because terms aren't accepted");
            navigate(`/onboarding/terms${location.search}`);
            return;
        }

        if (isPendingPractitioner && !profile.practitioner) {
            /* wait for other hook to create practitioner */
            return;
        }

        /* If we're not a practitioner, we're done with onboarding at this point. */
        if (!profile.practitioner) {
            navigate("/sessions");
            return;
        }

        /*console.log(profile.practitioner);*/
        /* Check if we need to handle any BAA stuff */
        if (profile.practitioner?.baaAcceptedAt) {
            navigate(`/practitioner${location.search}`);
            return;
        }

        if (
            profile.practitioner &&
            (!profile.practitioner?.legalLocation ||
                profile.practitioner.legalLocation === "UNKNOWN")
        ) {
            navigate(`/onboarding/practitioner/location${location.search}`);
            return;
        }

        if (profile.practitioner?.legalLocation === "USA") {
            navigate(`/onboarding/practitioner/baa${location.search}`);
            return;
        }

        /* We're all done! */
        navigate(`/practitioner${location.search}`);
    }, [navigate, location, profile, isPendingPractitioner]);

    // If we're creating a practitioner, then register them as soon as the profile is set up.
    useEffect(() => {
        if ((isPendingPractitioner || practitionerInviteToken) && profile) {
            if (!profile.practitioner) {
                registerPractitioner({
                    fullName: profile.fullName || "",
                    practitionerInviteToken: practitionerInviteToken,
                }).catch((error) => {
                    logger
                        .withError(error as Error)
                        .error(
                            "Practitioner registration failed in onboarding wrapper",
                        );
                });
            } else {
                setPendingPractitioner(false);
            }
        }
    }, [
        isPendingPractitioner,
        profile,
        practitionerInviteToken,
        navigateToNextOnboardingStep,
        registerPractitioner,
        setPendingPractitioner,
    ]);

    // Function to handle step completion
    const onStepComplete = (step: string) => {
        setCurrentStep(step);
        navigateToNextOnboardingStep();
        // The logic for navigation will be provided later
        logger.info(`Completed onboarding step: ${step}`);
    };

    // Context value
    const contextValue = {
        currentStep,
        onStepComplete,
    };

    if (isRegisteringPractitioner) {
        return <Layout title="Registering practitioner..." loading />;
    }

    return (
        <OnboardingContext.Provider value={contextValue}>
            <Outlet />
        </OnboardingContext.Provider>
    );
};

export default OnboardingWrapper;
